<script setup lang="ts">
// This is a mounting component for FormKitBuilderContainer.vue
// when the form is used as a block directly on a page (e.g. in Article page).
import { DsBlock } from '@coloplast/design-system';
import {
	useFormKitHelpers,
	type FormValues,
	type FormBuilderSuccessResponse
} from 'shared/composables/useFormKitHelpers';
import initCampaignIdSFDC from 'shared/vanilla/initSendCampaignIDtoSFDC';
import type { ViewModel } from './FormBuilderBlock.types'
import type { FormKitNode } from '@formkit/core';
import { nextTick, ref } from 'vue';
import Toaster from 'shared/components/toaster/toaster.vue';
import { useStore } from 'vuex';
import { each } from 'lodash';
import { type AxiosError } from 'axios';
import { formatApiMessages } from 'multisite/services/checkoutHelperService.js';
import { track_form_submission } from 'shared/services/tracking/formTrackingService';

export interface Props {
	vm: ViewModel;
}

const props = defineProps<Props>();

const store = useStore();

const { prepareFormData, submitForm } = useFormKitHelpers();

const showSuccessToast = ref(false);
const successResponseData = ref<FormBuilderSuccessResponse | null>(null);

function handleShowSuccessMessage(response: FormBuilderSuccessResponse) {
	if (response.displayPopupMessage && (response.successMessageTitle || response.successMessageDescription)) {
		showSuccessToast.value = false;
		// ensure toast can appear more than once
		nextTick(() => {
			showSuccessToast.value = true;
			successResponseData.value = response;
		});
	}
}

async function handleSubmit(
	formValues: FormValues,
	node: FormKitNode,
) {
	try {
		store.dispatch('showGlobalSpinner');

		const payload = prepareFormData(formValues);
		const response = await submitForm<FormBuilderSuccessResponse>(payload);

		track_form_submission(node.context?.attrs['data-gtm-form-name'] || 'no_data');

		if (props.vm.BaseViewModel.ShouldTrackSfdcCampaign) {
			await initCampaignIdSFDC(
				props.vm.CurrentBlock.CampaignId,
				props.vm.CurrentBlock.CampaignStatus
			);
		}

		if (response.data) {
			handleShowSuccessMessage(response.data);
		}

		node.reset();

		const redirectLink = props.vm.BaseViewModel.RedirectToPageLinkUrl;
		if (redirectLink) {
			window.location.assign(redirectLink);
		} else {
			store.dispatch('hideGlobalSpinner');
		}
	} catch (error) {
		store.dispatch('hideGlobalSpinner');

		const { response } = error as AxiosError<{ message?: string }>;

		if (response?.status === 400 && response?.data?.message) {
			const errorObj: Record<string, string> = {};
			each(formatApiMessages(response.data.message), ({ name, message }) => {
				errorObj[name] = message;
			});
			node.setErrors([], errorObj);
		}
	}
}

defineExpose({
	handleSubmit, // for tests
})
</script>

<template>
	<DsBlock>
		<div class="c-formkit-container form-builder-block__form-container">
			<slot name="form-kit" :submit-handler="handleSubmit" />
		</div>
	</DsBlock>

	<Toaster
		v-if="showSuccessToast"
		ref="success-toast"
		class="c-formkit-container__toaster"
		:headline="successResponseData?.successMessageTitle || ''"
		:description="successResponseData?.successMessageDescription || ''"
		:timeout="successResponseData?.successMessageTimeout?.toString()"
	/>
</template>

<style scoped lang="scss">
.form-builder-block {
	&__form-container {
		margin: 0 auto;
		max-width: 600px;
	}
}
</style>
